// /dashboard — vista read-only de metricas por partner.
//
// Incluye:
//   - Dropdown de partners (persiste seleccion en sessionStorage).
//   - Cuentas verificadas del partner, con el total y las pendientes debajo.
//   - 4 metric cards (acceso pago / payments 30d / success rate / facturacion).
//   - Breakdown de subs por plan (tabla simple).
//   - Alerts computadas por el backend. Los cobros pendientes NO van aca: los
//     cuenta el contador de "Requiere atención" del menu.
//   - Timestamp de cuando se genero el snapshot.
//
// TanStack Query cachea 30s (default del root); el boton "Refresh" invalida
// y re-fetchea. Su flecha abre el refresco automatico (30 s a 15 min), que se
// recuerda por navegador y se pausa con la pestaña oculta.

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchDashboard, listPartners } from '@/lib/api';
import MetricCard from '@/components/MetricCard';
import { fieldCls } from '@/lib/form-styles';
import RefreshControl, { useAutoRefresh } from '@/components/RefreshControl';

const PARTNER_STORAGE_KEY = 'authsys-admin-selected-partner';
const AUTO_REFRESH_STORAGE_KEY = 'authsys-admin-dashboard-auto-refresh';

function AlertItem({
  level,
  message,
  href,
}: {
  level: string;
  message: string;
  href?: string | undefined;
}): JSX.Element {
  const cls =
    level === 'critical'
      ? 'border-status-error/40 bg-status-error/10 text-status-error'
      : level === 'warning'
        ? 'border-status-warning/40 bg-status-warning/10 text-status-warning'
        : 'border-status-info/40 bg-status-info/10 text-status-info';
  return (
    <li
      role="status"
      className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-2 ${cls}`}
    >
      <span className="text-xs uppercase font-semibold min-w-[5rem]">{level}</span>
      <span className="flex-1">{message}</span>
      {href ? (
        <Link to={href} className="shrink-0 font-medium underline underline-offset-2">
          Ver y resolver
        </Link>
      ) : null}
    </li>
  );
}

/**
 * Numero dentro de una linea de detalle. El detalle de una MetricCard va en
 * gris chico; esto devuelve las cifras al color y al peso del texto normal para
 * que se lean sin tener que buscarlas, sin competir con el numero grande.
 */
function Num({ children }: { children: ReactNode }): JSX.Element {
  return (
    <strong className="font-semibold tabular-nums text-ink dark:text-ink-inverted">
      {children}
    </strong>
  );
}

/**
 * Barra de proporcion verificadas / sin verificar.
 *
 * El verde es el relleno y el ambar es la pista: con dos divs en un flex, el
 * segundo necesita saber su ancho o se estira, y este modo tiene un solo numero
 * que calcular. La barra es decorativa — los dos numeros estan escritos abajo en
 * texto — asi que va con aria-hidden en vez de duplicarlos para un lector de
 * pantalla.
 */
function VerifiedBar({ ratio }: { ratio: number }): JSX.Element {
  const pct = Math.min(100, Math.max(0, ratio * 100));
  return (
    <div
      aria-hidden="true"
      className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-status-warning"
    >
      <div className="h-full rounded-full bg-status-success" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Tasa en es-AR con un decimal, o "—" si nadie intento. */
function formatRate(rate: number | null): string {
  return rate === null
    ? '—'
    : `${(Math.round(rate * 1000) / 10).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
}

/** Mes en castellano a partir de un desplazamiento desde el mes en curso. */
function monthName(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleString('es-AR', { month: 'long' });
}

/** Una ventana del success rate: "Hoy: 92,3% · 12 de 13 personas". */
function RateLine({
  label,
  payers,
}: {
  label: string;
  payers: import('@/lib/api').DashboardPayers;
}): JSX.Element {
  return (
    <p>
      {label}:{' '}
      {payers.attempted > 0 ? (
        <>
          <Num>{formatRate(payers.success_rate)}</Num> · <Num>{payers.succeeded.toLocaleString('es-AR')}</Num> de{' '}
          <Num>{payers.attempted.toLocaleString('es-AR')}</Num> persona{payers.attempted === 1 ? '' : 's'}
        </>
      ) : (
        'nadie intentó pagar'
      )}
    </p>
  );
}

function formatMoney(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency ?? 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ''}`.trim();
  }
}

export default function Dashboard(): JSX.Element {
  const qc = useQueryClient();
  const [partnerId, setPartnerId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(PARTNER_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const partnersQ = useQuery({
    queryKey: ['admin-partners'],
    queryFn: listPartners,
  });

  // Al cargar la lista, auto-seleccionar el primer partner si aun no hay uno.
  useEffect(() => {
    if (!partnerId && partnersQ.data && partnersQ.data.length > 0) {
      const first = partnersQ.data[0]!;
      setPartnerId(first.partner_id);
      try {
        sessionStorage.setItem(PARTNER_STORAGE_KEY, first.partner_id);
      } catch {
        // ignore
      }
    }
  }, [partnerId, partnersQ.data]);

  const [autoRefresh, setAutoRefresh] = useAutoRefresh(AUTO_REFRESH_STORAGE_KEY);

  const dashboardQ = useQuery({
    queryKey: ['admin-dashboard', partnerId],
    queryFn: () => fetchDashboard(partnerId!),
    enabled: !!partnerId,
    refetchInterval: autoRefresh.enabled ? autoRefresh.ms : false,
  });

  function onPartnerChange(next: string): void {
    setPartnerId(next);
    try {
      sessionStorage.setItem(PARTNER_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }

  function onRefresh(): void {
    if (!partnerId) return;
    qc.invalidateQueries({ queryKey: ['admin-dashboard', partnerId] });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 data-help-page="dashboard.page" data-help="dashboard.page" className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-ink-muted">
            Métricas por partner (últimos 30 días).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3" data-help="dashboard.partner">
            <label htmlFor="partner-select" className="text-sm text-ink-muted">
              Partner:
            </label>
            <select
              id="partner-select"
              value={partnerId ?? ''}
              onChange={(e) => onPartnerChange(e.target.value)}
              disabled={partnersQ.isLoading || !partnersQ.data?.length}
              className={fieldCls({ size: 'lg', block: false })}
            >
              {partnersQ.data?.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.display_name} ({p.status})
                </option>
              ))}
            </select>
          </div>
          <div data-help="dashboard.refresh">
            <RefreshControl
              onRefresh={onRefresh}
              fetching={dashboardQ.isFetching}
              disabled={!partnerId}
              auto={autoRefresh}
              onAutoChange={setAutoRefresh}
            />
          </div>
        </div>
      </header>

      {partnersQ.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-4 text-sm text-status-error">
          No pudimos cargar la lista de partners. Refresca la página o verificá el backend.
        </div>
      ) : null}

      {dashboardQ.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-4 text-sm text-status-error">
          No pudimos cargar las métricas de este partner.
        </div>
      ) : null}

      {dashboardQ.data ? <DashboardBody data={dashboardQ.data} /> : null}
      {dashboardQ.isLoading ? <p className="text-sm text-ink-muted">Cargando métricas…</p> : null}
    </div>
  );
}

function DashboardBody({
  data,
}: {
  data: import('@/lib/api').DashboardSnapshot;
}): JSX.Element {
  const { subscriptions, payments_30d: payments, accounts, alerts, webhooks, partner, generated_at } = data;
  const access = data.paid_access ?? null;
  const billing = data.billing ?? null;

  // Proporcion de verificadas sobre el total. null con padron vacio: "0%" ahi
  // sugeriria que nadie verifico, cuando lo cierto es que no hay a quien contar
  // — y sin nadie a quien contar tampoco hay barra que dibujar.
  const verifiedRatio = accounts.total > 0 ? accounts.verified / accounts.total : null;
  // Formateado en es-AR (coma decimal) para no desentonar con los numeros que
  // tiene al lado, que ya salen con separador de miles local.
  const verifiedPct =
    verifiedRatio !== null
      ? (verifiedRatio * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })
      : null;
  const unverified = accounts.total - accounts.verified;

  // Por persona: de la gente que intento pagar, cuantos lo lograron. Los
  // reintentos de una misma persona no la cuentan dos veces. El numero grande
  // es el historico (el valor de referencia); semana y dia van abajo para
  // compararse contra el. `?? null` por si el portal le pega a una Lambda vieja.
  const rates = data.success_rates ?? null;
  const payerRate = rates?.historic.success_rate ?? null;
  const successRatePct = formatRate(payerRate);
  const successEmphasis: 'success' | 'warning' | 'error' | 'default' =
    payerRate === null
      ? 'default'
      : payerRate >= 0.95
        ? 'success'
        : payerRate >= 0.9
          ? 'warning'
          : 'error';

  return (
    <div className="space-y-6">
      <section aria-label="Alertas activas" data-help="dashboard.alerts">
        {alerts.length > 0 ? (
          <ul className="space-y-2">
            {alerts.map((a) => (
              <AlertItem key={a.code} level={a.level} message={a.message} href={a.href} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-muted">Sin alertas activas.</p>
        )}
      </section>

      {/*
        Tres columnas: las cuentas ocupan una entera y las otras dos llevan dos
        tarjetas apiladas cada una. No es solo llenar el hueco que dejaba la
        tarjeta de cuentas — cada columna es un tema: quienes son, que
        contrataron, y cuanto se cobro.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <section aria-label="Cuentas verificadas" data-help="dashboard.accounts">
          <MetricCard
            hero
            label="Cuentas verificadas"
            value={accounts.verified.toLocaleString('es-AR')}
            detail={
              <>
                {verifiedRatio !== null ? <VerifiedBar ratio={verifiedRatio} /> : null}
                <p>
                  Confirmaron su correo
                  {verifiedPct !== null ? <> (<Num>{verifiedPct}%</Num>)</> : null}
                </p>
                <p>
                  <Num>{unverified.toLocaleString('es-AR')}</Num> sin verificar
                </p>
                <p className="opacity-70">
                  sobre un total de <Num>{accounts.total.toLocaleString('es-AR')}</Num> en{' '}
                  {partner.display_name}
                </p>
                <p className="opacity-60">
                  Actualizado {new Date(accounts.as_of).toLocaleString()}
                </p>
              </>
            }
          />
        </section>

        <section
          aria-label="Métricas principales"
          data-help="dashboard.metrics"
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-2"
        >
          {/*
            Personas, no suscripciones: quien puede ver contenido pago hoy y por
            que camino. Las cubetas no se pisan (pago > cupon > Hall Pass), asi
            que suman el numero grande.
          */}
          <MetricCard
            label="Cuentas pagas + Hall Pass"
            value={(access?.total ?? subscriptions.paid_active ?? subscriptions.total_active).toLocaleString('es-AR')}
            detail={
              <>
                {access ? (
                  <>
                    <p>
                      <Num>{access.paid.toLocaleString('es-AR')}</Num> con plan pago
                    </p>
                    <p>
                      <Num>{access.hall_pass.toLocaleString('es-AR')}</Num> por Hall Pass
                    </p>
                    {access.coupon > 0 ? (
                      <p>
                        <Num>{access.coupon.toLocaleString('es-AR')}</Num> por cupón
                      </p>
                    ) : null}
                  </>
                ) : null}
                {subscriptions.expired_last_30d > 0 ? (
                  <p className="opacity-70">
                    {subscriptions.expired_last_30d.toLocaleString('es-AR')} expiraron en 30d
                  </p>
                ) : null}
              </>
            }
          />
          <MetricCard
            label="Payments 30d"
            value={payments.succeeded_count.toLocaleString('es-AR')}
            emphasis={payments.succeeded_count > 0 ? 'success' : 'default'}
            detail="cobros en los últimos 30 días"
          />
          <MetricCard
            label="Success rate"
            value={successRatePct}
            detail={
              rates ? (
                <>
                  <RateLine label="Histórico" payers={rates.historic} />
                  <RateLine label="Esta semana" payers={rates.week} />
                  <RateLine label="Hoy" payers={rates.today} />
                </>
              ) : null
            }
            emphasis={successEmphasis}
          />
          {/*
            Lo cobrado sale del ledger (neto de anulaciones y reintegros), igual
            que Reportes. "Falta cobrar" es una estimacion simple: las
            renovaciones que vencen antes de fin de mes, si todas se cobran.
          */}
          <MetricCard
            label="Facturación"
            value={billing ? formatMoney(billing.collected_this_month, billing.currency) : '—'}
            detail={
              billing ? (
                <>
                  <p>cobrado en {monthName(0)}</p>
                  <p>
                    Falta cobrar (estimado):{' '}
                    <Num>{formatMoney(billing.pending_this_month.amount, billing.currency)}</Num>
                    {billing.pending_this_month.subscriptions > 0 ? (
                      <>
                        {' '}
                        en {billing.pending_this_month.subscriptions.toLocaleString('es-AR')} renovaci
                        {billing.pending_this_month.subscriptions === 1 ? 'ón' : 'ones'}
                      </>
                    ) : null}
                  </p>
                  <p>
                    Mes pasado ({monthName(-1)}):{' '}
                    <Num>{formatMoney(billing.collected_last_month, billing.currency)}</Num>
                  </p>
                  <p>
                    Histórico: <Num>{formatMoney(billing.collected_total, billing.currency)}</Num>
                  </p>
                </>
              ) : null
            }
          />
        </section>
      </div>

      <section
        aria-label="Breakdown por plan"
        data-help="dashboard.plans"
        className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border"
      >
        <header className="px-5 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h2 className="text-sm font-medium">Suscripciones activas por plan</h2>
        </header>
        <div className="p-5">
          {subscriptions.breakdown_by_plan.length === 0 ? (
            <p className="text-sm text-ink-muted">Sin suscripciones activas.</p>
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
              {subscriptions.breakdown_by_plan.map((p) => (
                <li key={p.plan_code} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{p.plan_name}</span>
                    <span className="text-ink-muted font-mono text-xs ml-2">({p.plan_code})</span>
                  </div>
                  <span className="tabular-nums">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <footer className="text-xs text-ink-muted flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Partner: <strong className="text-ink dark:text-ink-inverted">{partner.display_name}</strong> ({partner.status})
        </span>
        <span>Generado: {new Date(generated_at).toLocaleString()}</span>
        {!webhooks.available ? (
          <span title={webhooks.note}>Webhook metrics: no disponibles (M9.2 TODO)</span>
        ) : null}
      </footer>
    </div>
  );
}
